import { useState, useRef } from 'react';
import { findLastIndex } from '../util/index.js';

var useListKeyboardInteractions = function (_a) {
    var _b;
    var filteredItems = _a.filteredItems, itemRefs = _a.itemRefs, multiple = _a.multiple, onChange = _a.onChange, selectedIndices = _a.selectedIndices;
    // When first tabbing to the list, the first selected option should receive focus,
    // or the first option if none are selected. When tabbing to a list that has been
    // interacted with, the last focused item should receive focus.
    var _c = useState((_b = selectedIndices[0]) !== null && _b !== void 0 ? _b : 0), _tabFocusIndex = _c[0], setTabFocusIndex = _c[1];
    // When selecting multiple items with Shift+Click, this is
    // the anchor index at which the selected range starts or ends.
    var rangeAnchorRef = useRef(-1);
    var getFocusableIndex = function (direction, i) {
        if (i === void 0) { i = -1; }
        switch (direction) {
            case "after": {
                var next = filteredItems.findIndex(function (_a) {
                    var index = _a.index, item = _a.item;
                    return !item.disabled && index > i;
                });
                return next > -1 ? next : getFocusableIndex("first");
            }
            case "before": {
                var prev = findLastIndex(filteredItems, function (_a) {
                    var index = _a.index, item = _a.item;
                    return !item.disabled && index < i;
                });
                return prev > -1 ? prev : getFocusableIndex("last");
            }
            case "first":
                return filteredItems.findIndex(function (_a) {
                    var item = _a.item;
                    return !item.disabled;
                });
            case "last":
                return findLastIndex(filteredItems, function (_a) {
                    var item = _a.item;
                    return !item.disabled;
                });
        }
    };
    /**
     * Returns the item index that should receive focus when tabbing
     * to the list. Defaults to the first focusable item if the
     * target item has been (temporarily) removed from the list.
     */
    var getTabFocusIndex = function () {
        return filteredItems.some(function (_a) {
            var index = _a.index, item = _a.item;
            return index === _tabFocusIndex && !item.disabled;
        })
            ? _tabFocusIndex
            : getFocusableIndex("first");
    };
    var handleKeyDown = function (index, e) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/listbox_role#keyboard_interactions
        if (e.key === " " || e.key === "Enter") {
            onChange(selectedIndices.includes(index) ? "remove" : "add", [index]);
        }
        else if (e.key === "ArrowDown") {
            (_b = (_a = itemRefs.current) === null || _a === void 0 ? void 0 : _a[getFocusableIndex("after", index)]) === null || _b === void 0 ? void 0 : _b.focus();
        }
        else if (e.key === "ArrowUp") {
            (_d = (_c = itemRefs.current) === null || _c === void 0 ? void 0 : _c[getFocusableIndex("before", index)]) === null || _d === void 0 ? void 0 : _d.focus();
        }
        else if (e.key === "End") {
            (_f = (_e = itemRefs.current) === null || _e === void 0 ? void 0 : _e[getFocusableIndex("last")]) === null || _f === void 0 ? void 0 : _f.focus();
        }
        else if (e.key === "Home") {
            (_h = (_g = itemRefs.current) === null || _g === void 0 ? void 0 : _g[getFocusableIndex("first")]) === null || _h === void 0 ? void 0 : _h.focus();
        }
        else if (e.key === "Shift") {
            rangeAnchorRef.current = index;
        }
        else if (e.key === "a" && e.ctrlKey && multiple) {
            // (De)select all visible items.
            var isSomeVisibleItemDeselected = filteredItems.some(function (_a) {
                var i = _a.index, item = _a.item;
                return !item.disabled && !selectedIndices.includes(i);
            });
            onChange(isSomeVisibleItemDeselected ? "add" : "remove", filteredItems
                .filter(function (_a) {
                var item = _a.item;
                return !item.disabled;
            })
                .map(function (_a) {
                var i = _a.index;
                return i;
            }));
        }
        else {
            return;
        }
        // Prevent any scroll container from scrolling down, and
        // other default behaviors of handled keys.
        e.preventDefault();
    };
    var handleKeyUp = function (_index, e) {
        if (e.key === "Shift") {
            // Unset the range anchor if the Shift key is released.
            // This is necessary because the user might focus on another
            // element that isn't a list item. The next time they try to
            // Shift+Click, the previous anchor shouldn't be used.
            rangeAnchorRef.current = -1;
            e.preventDefault();
        }
    };
    return {
        getFocusableIndex: getFocusableIndex,
        getTabFocusIndex: getTabFocusIndex,
        handleKeyDown: handleKeyDown,
        handleKeyUp: handleKeyUp,
        rangeAnchorRef: rangeAnchorRef,
        setTabFocusIndex: setTabFocusIndex,
    };
};

export default useListKeyboardInteractions;
